import React, { useCallback } from 'react';
import {
  IconButton,
  Tooltip,
  FormControl,
  FormHelperText,
  InputLabel,
  Box,
  makeStyles,
} from '@material-ui/core';
import { RemoveCircle as RemoveCircleIcon, CloudUpload as CloudUploadIcon } from '@material-ui/icons';
import _, { isFunction } from 'lodash';
import { useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import QCXFileListDisplay from '../file-list/QCXFileListDisplay';
import QCXFinalInputFileAlt from './QCXFInalInputFileAlt';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(1),
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  uploadButton: {
    marginLeft: theme.spacing(1),
    alignSelf: 'flex-start',
  },
  fileListContainer: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
}));

const QCXFinalInputFileWithRef = React.forwardRef((props, ref) => (
  <QCXFinalInputFileAlt {...props} ref={ref} />
));

export default function QCXFinalCustomInputFileAlt({ ...props }) {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    id,
    name,
    label,
    accept,
    tooltipTitle,
    fileGetter,
    fileNameGetter,
    defaultSelectionText = t('com.muralis.qcx.arquivo.nenhumArquivoSelecionado'),
    disableRemoveControl = false,
    inputRef,
    required,
    multiple = false,
    disabled,
    helperText,
    meta,
    variant = 'chips',
    ...restProps
  } = props;

  const form = useForm();

  const handleRemoveLoadedFile = useCallback((event) => {
    event?.stopPropagation();
    form.change(name, multiple ? [] : undefined);
  }, [form, name, multiple]);

  // Na parte do return, ajuste o JSX assim:
  return (
    <FormControl fullWidth error={meta?.touched && !!meta?.error} className={classes.root}>
      {label && (
        <InputLabel shrink className={classes.label}>
          {label}
        </InputLabel>
      )}

      <QCXFinalInputFileWithRef
        id={id}
        label={label}
        name={name}
        accept={accept}
        tooltipTitle={tooltipTitle}
        fileGetter={fileGetter}
        disabled={disabled}
        multiple={multiple}
        required={required}
        inputRef={inputRef}
        {...restProps}
      >
        {() => (
          // Container principal
          <Box display="flex" alignItems="center" gap={1}>
            {/* Lista de arquivos */}
            <Box flex={1}>
              <QCXFileListDisplay
                variant={variant}
                emptyText={defaultSelectionText}
                fileNameGetter={fileNameGetter}
              />
            </Box>

            {/* Container de botões */}
            <Box display="flex" alignItems="center" gap={0.5}>
              <Tooltip title={tooltipTitle} arrow>
                <IconButton
                  component="label" // Alterado para 'label'
                  htmlFor={id} // Adicionado htmlFor
                  color="primary"
                  disabled={disabled}
                  aria-label={tooltipTitle} // Melhoria de acessibilidade
                >
                  <CloudUploadIcon fontSize="medium" />
                </IconButton>
              </Tooltip>

              {!disableRemoveControl && (
                <Tooltip title={t('com.muralis.qcx.arquivo.removerArquivoSelecionado')} arrow>
                  <IconButton
                    onClick={handleRemoveLoadedFile}
                    disabled={disabled}
                  >
                    <RemoveCircleIcon fontSize="small" color={disabled ? 'disabled' : 'error'} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}
      </QCXFinalInputFileWithRef>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {meta?.touched && meta?.error && <FormHelperText error>{meta.error}</FormHelperText>}
    </FormControl>
  );
}